import React, { ReactElement, useId } from "react";
import { styled } from "@mui/system";
import {
  FormControl,
  IconButton,
  Theme,
  SxProps,
  Box,
  useTheme,
} from "@mui/material";
import { defaultColorTheme } from "theme/default";
import { TextCus } from "components";
import { useTranslation } from "react-i18next";
import { ThemeOption } from "types";
import { useUser } from "hooks";

const CustomFormControl = styled(FormControl)<{
  warning?: boolean;
  error?: boolean;
}>(({ warning, error }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: error
        ? defaultColorTheme.warning
        : warning
          ? defaultColorTheme.warning
          : defaultColorTheme.textMain, // Outline color based on error or warning
    },
    "&:hover fieldset": {
      borderColor: error
        ? defaultColorTheme.warning
        : warning
          ? defaultColorTheme.warning
          : defaultColorTheme.textMain, // Outline color on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: error
        ? defaultColorTheme.warning
        : warning
          ? defaultColorTheme.warning
          : defaultColorTheme.textMain, // Outline color when focused
    },
  },
  "& .MuiInputLabel-root": {
    color: error
      ? defaultColorTheme.warning
      : warning
        ? defaultColorTheme.warning
        : defaultColorTheme.textMain,
    "&.Mui-focused": {
      color: error
        ? defaultColorTheme.warning
        : warning
          ? defaultColorTheme.warning
          : defaultColorTheme.textMain,
    },
  },
}));

interface CustomFormControlInputProps {
  warning?: boolean;
  error?: boolean;
  label: string;
  value?: string;
  errorMessage?: string;
  setValue?: (value: string) => void;
  type?: string;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  icon?: ReactElement;
  onClickIcon?: () => void;
  placeholder?: string;
  disable?: boolean;
  isShowLabel?: boolean;
  height?: string;
  min?: number;
  max?: number;
  props?: any
}

const CustomFormControlInput: React.FC<CustomFormControlInputProps> = ({
  warning,
  error,
  label,
  setValue,
  errorMessage,
  type = "text",
  fullWidth,
  icon,
  disable,
  onClickIcon,
  isShowLabel = true,
  height = "50px",
  ...props
}) => {
  const id = useId();
  const { t } = useTranslation();
  const { mode } = useUser();
  const theme = useTheme() as ThemeOption;

  return (
    <CustomFormControl
      variant="outlined"
      warning={warning}
      error={error}
      fullWidth={fullWidth}
    >
      {isShowLabel && (
        <TextCus
          color={theme?.colorSchemes?.[mode]?.textMain}
          fontWeight={"600"}
          fontSize={"14px"}
          mb={1}
        >
          {label}
        </TextCus>
      )}
      <Box width={"100%"} position={"relative"}>
        <input
          autoComplete="off"
          id={id}
          type={type}
          disabled={disable}
          onChange={(e) => setValue?.(e.target.value)}
          placeholder={t(label)}
          style={{
            backgroundColor: disable ? "rgba(194, 191, 191, 0.19)" : "#f5f5f5",
            outline: "none",
            height: height,
            padding: "0 20px",
            borderRadius: "5px",
            border: "none",
            color: theme?.colorSchemes?.[mode]?.textMain,
            fontSize: "13px",
            width: "100%",
          }}
          {...props}
        />
        {icon && (
          <Box position={"absolute"} right="5px" bottom={"6px"}>
            <IconButton onClick={onClickIcon} sx={{ padding: 1 }}>
              {icon}
            </IconButton>
          </Box>
        )}
      </Box>
      {errorMessage && (
        <TextCus
          marginTop={"5px"}
          useI18n
          color={
            error
              ? defaultColorTheme.error
              : warning
                ? defaultColorTheme.warning
                : defaultColorTheme.textMain
          }
        >
          {errorMessage}
        </TextCus>
      )}
    </CustomFormControl>
  );
};

export default CustomFormControlInput;
